import { OrderedMap, Set, Map } from 'immutable';
import Occasion from '../helpers/Occasion';
import BaseObject from './BaseObject';
import Contact from './Contact';
import Group from './Group';
import Collection from './Collection';
import { SharedContact } from './SharedContact';
import { SharedGroup } from './SharedGroup';
import { ID } from '../types';
import { preProcessDiffMD } from '../helpers/markdown';

export type ActivityItemType =
  | 'announcement'
  | 'birthday'
  | 'diff'
  | 'event'
  | 'import'
  | 'memorial'
  | 'new-member'
  | 'news'
  | 'post'
  | 'reconnect'
  | 'reminder'
  | 'shared'
  | 'duplicates'
  | 'new-workspace-member'
  | 'whats-new';

export type ActivityItemStatus = 'ACTIVE' | 'DISMISSED';

export type ImportSource =
  | 'apple-contacts'
  | 'apple-messages'
  | 'calendar'
  | 'contacts'
  | 'email'
  | 'facebook'
  | 'linkedin'
  | 'twitter'
  | 'bulk_import'
  | 'zapier'; // TODO: et al...
export type ActivityAnnouncementSubtype =
  | 'connect-integrations'
  | 'intro-video'
  | 'welcome-message'
  | 'groups'
  | 'home-feed'
  | 'duplicates'
  | 'library';

type PostSource = 'linkedin'; // | 'otherIntegration'...
type DiffSource = 'twitter';

type LinkedInPostSubtype = 'announcement' | undefined; // 'announcement' | 'celebration' | undefined;
interface LinkedInPostImage {
  thumbnail: string;
  type: 'image' | 'video';
}

export type TwitterDiffSubtype = 'website' | 'location' | 'bio';

type AnnouncementType = 'anniversary' | 'new-job' | 'promotion';
interface AnnouncementMetadata {
  years?: number;
  jobTitle?: string;
  company?: string;
  companyHref?: string;
}

export type ReconnectContactStatus = 'active' | 'dismissed' | 'snoozed';

// TODO: split this out so that Metadata is typed for each type of ActivityItem
export interface ActivityItemMetadata {
  importSource?: ImportSource;
  importName?: string;
  importTotal?: number;
  sharedBy?: Contact;
  sharedContact?: Contact;
  sharedGroup?: Group;
  contact?: Contact;
  contacts?: Contact[];
  contactStatus?: Record<string, ReconnectContactStatus>;
  content?: string;
  noteId?: ID;
  imageSrc?: string;
  link?: string;
  title?: string;
  outlet?: string;
  terms?: string[];
  startDate?: Occasion;
  endDate?: Occasion;
  memorial?: Occasion;
  postSubtype?: LinkedInPostSubtype; // | 'OtherIntegrationPostSubtype'...
  media?: LinkedInPostImage[];
  source?: PostSource | DiffSource;
  linkedInAnnouncement?: { type?: AnnouncementType; metadata?: AnnouncementMetadata };
  subtype?: ActivityAnnouncementSubtype | TwitterDiffSubtype;
  groupId?: number;
  groupName?: string;
  count?: number;
  duplicateCount?: number;
  potentialDuplicateCount?: number;
  after?: string;
  before?: string;
  linkTitle?: string;
}

interface MergedContact {
  contact: Contact;
  sourceIDs: Set<ID>;
}

// map of original merged contact IDs to their respective merged/consolidated contact ID
export type SourceContactIDToMergedContactIDMap = Map<ID, ID>;
export type MergedContactsMap = Map<ID, MergedContact>;

const sidebarTitleByType: Partial<
  Record<ActivityItemType, (activityItem: ActivityItem) => string>
> = {
  import: (a) => `Import from ${a.metadata.importName}`,
  reconnect: () => 'Reconnect',
  event: (a) => a.metadata.title || 'Meeting',
  shared: (a) => a.metadata.sharedGroup?.title,
};

const displayEventTimeSpan = (metadata: ActivityItemMetadata) => {
  if (metadata.startDate && metadata.endDate) {
    return `${metadata.startDate.format('h:mm a')} - ${metadata.endDate.format('h:mm a')} • `;
  } else if (metadata.startDate) {
    return `${metadata.startDate.format('h:mm a')} • `;
  }
  return '';
};

const sidebarSubtitleByType: Partial<
  Record<ActivityItemType, (activityItem: ActivityItem) => string>
> = {
  import: (a) =>
    `Synced on ${a.date.format('MMM d')}${
      a.date.isThisYear ? '' : a.date.format(' yyyy')
    } • ${a.date.format('h:mm a')}`,
  reconnect: () => 'Strengthen your relationships by staying in touch',
  event: (a) => `${displayEventTimeSpan(a.metadata)}${a.metadata.contacts?.length} people`,
  shared: (a) => `Shared by ${a.metadata.sharedBy?.displayName}`,
};

export class ActivityItem extends BaseObject {
  type: ActivityItemType;
  status: ActivityItemStatus;
  seen: boolean;
  date: Occasion;
  metadata: ActivityItemMetadata = {};
  dynamicHeight?: boolean;
  fixedHeight?: number;

  constructor(props: any) {
    super(props);
    this.type = props.itemType || props.type;
    this.status = props.status;
    this.seen = props.seen;
    this.date = new Occasion(props.itemDate || props.date);

    if (
      ['birthday', 'reminder', 'new-member', 'news', 'post', 'diff', 'memorial'].includes(this.type)
    ) {
      this.metadata.contact = new Contact(props.metadata.contact || {});
    }

    if (['reconnect', 'import', 'event'].includes(this.type)) {
      this.metadata.contacts = props.metadata.contacts?.map((contact: any) => new Contact(contact));
      if (
        (!props.metadata.contacts || props.metadata.contacts?.length === 0) &&
        props.metadata.contact
      ) {
        const contact = new Contact(props.metadata.contact);
        this.metadata.contacts = [contact];
        this.metadata.contact = contact;
      }
    }

    if (['reminder', 'announcement', 'post', 'whats-new'].includes(this.type)) {
      this.metadata.content = props.metadata.content;
    }

    if (this.type === 'reminder') {
      this.metadata.noteId = props.metadata.noteId;
    }

    if (this.type === 'news') {
      this.metadata.imageSrc = props.metadata.image || props.metadata.imageSrc;
      this.metadata.link = props.metadata.link;
      this.metadata.title = props.metadata.title;
      this.metadata.outlet = props.metadata.source || props.metadata.outlet;
    }

    if (this.type === 'reconnect') {
      this.metadata.contactStatus = props.metadata.contactStatus || props.metadata.contact_status;
    }

    if (this.type === 'import') {
      this.metadata.importSource = props.metadata.importSource || props.metadata.import_source;
      this.metadata.importName = props.metadata.importName || props.metadata.import_name;

      if (this.metadata.importSource === 'bulk_import') {
        this.metadata.importName = 'CSV';
      }

      this.metadata.groupId = props.metadata.groupId;
      this.metadata.groupName = props.metadata.groupName;
      const contactsLength = props.metadata.contacts?.length || 0;
      const importTotal = props.metadata.importTotal || props.metadata.import_total || 0;

      let total = importTotal > contactsLength ? importTotal : contactsLength;
      if (props.metadata.contact) {
        total = 1;
      }

      const singleContactTotal = props.metadata.contact ? 1 : 0;
      this.metadata.importTotal = total > 0 ? total : singleContactTotal;
    }

    if (this.type === 'event') {
      this.metadata.startDate = props.metadata.startDate && new Occasion(props.metadata.startDate);
      this.metadata.endDate = props.metadata.endDate && new Occasion(props.metadata.endDate);
      this.metadata.title = props.metadata.content || props.metadata.title;
    }

    if (this.type === 'announcement') {
      this.metadata.subtype = props.metadata.subtype;
      this.metadata.terms = props.metadata.terms;
      this.metadata.title = props.metadata.title;
      this.metadata.imageSrc = props.metadata.image || props.metadata.imageSrc;
      this.metadata.link = props.metadata.link;
      this.metadata.outlet = props.metadata.outlet || props.metadata.source;
      this.metadata.linkTitle = props.metadata.linkTitle;

      if (this.metadata.subtype === 'library') {
        this.fixedHeight = 132;
      }
    }

    if (this.type === 'shared') {
      const sharedContact = props.metadata.contact || props.metadata.sharedContact;
      const sharedGroup = props.metadata.group || props.metadata.sharedGroup;
      this.metadata.sharedBy = new Contact(props.metadata.sharedBy || {});
      this.metadata.sharedContact = sharedContact && new SharedContact(sharedContact);
      this.metadata.sharedGroup = sharedGroup && new SharedGroup(sharedGroup);
    }

    if (this.type === 'post') {
      this.metadata.postSubtype = props.metadata.postSubtype;
      this.metadata.source = props.metadata.source;
      this.metadata.link = props.metadata.link;
      this.metadata.media = props.metadata.media;
      this.metadata.linkedInAnnouncement = props.metadata.linkedInAnnouncement;
    }

    if (this.type === 'whats-new') {
      this.metadata.link = props.metadata.link;
    }

    if (this.type === 'diff') {
      this.metadata.subtype = props.metadata.subtype;
      this.metadata.content = preProcessDiffMD(props.metadata.content);
      this.metadata.before = props.metadata.before;
      this.metadata.after = props.metadata.after;
      this.metadata.source = props.metadata.source;
      this.metadata.link = props.metadata.link || props.metadata.accountLink;
    }

    if (this.type === 'memorial') {
      this.metadata.memorial = new Occasion(props.metadata.memorial);
    }

    if (this.type === 'duplicates') {
      this.metadata.count = props.metadata.count;
      this.metadata.duplicateCount = props.metadata.duplicateCount;
      this.metadata.potentialDuplicateCount = props.metadata.potentialDuplicateCount;
    }
  }

  get sidebarTitle(): string {
    return sidebarTitleByType[this.type]?.(this) || '';
  }

  get sidebarSubtitle(): string {
    return sidebarSubtitleByType[this.type]?.(this) || '';
  }

  get contact(): Contact {
    if (this.metadata.contact) {
      return this.metadata.contact;
    } else if (
      this.metadata.contacts?.length === 1 &&
      ['event', 'reconnect', 'import'].includes(this.type)
    ) {
      return this.metadata.contacts[0];
    }
    return undefined;
  }

  contactStatus(contactID: ID): ReconnectContactStatus {
    return this.metadata.contactStatus?.[contactID] || 'active';
  }

  rowHeightForWidth(width: number): number | undefined {
    if (this.metadata.subtype === 'connect-integrations') {
      if (width < 1112) {
        return 138;
      }

      return 120;
    }

    if (this.metadata.subtype === 'home-feed') {
      if (width < 611) {
        return 124;
      }

      if (width < 1152) {
        return 106;
      }

      return 88;
    }

    if (this.metadata.subtype === 'groups') {
      if (width < 1048) {
        return 105;
      }

      return 87;
    }

    if (this.metadata.subtype === 'intro-video') {
      if (width < 605) {
        return 181;
      }

      return 163;
    }

    if (this.metadata.subtype === 'duplicates') {
      if (width < 805) {
        return 105;
      }

      return 87;
    }

    if (this.metadata.subtype === 'welcome-message') {
      if (width < 714) {
        return 105;
      }

      return 87;
    }

    return undefined;
  }

  get filteredContacts(): Contact[] {
    if (this.type === 'reconnect') {
      return this.metadata.contacts.filter(
        (contact) => this.contactStatus(contact.id) === 'active',
      );
    }

    return this.metadata.contacts || [];
  }

  get hasContactList(): boolean {
    return this.filteredContacts.length > 1 || !!this.metadata.sharedGroup;
  }
}

export default class Activity extends Collection<ActivityItem> {
  _canLoadMore: boolean;
  archivedContactIDs: Set<ID>;
  sourceContactIDToMergedContactIDMap: SourceContactIDToMergedContactIDMap;
  mergedContactsMap: MergedContactsMap;

  constructor(props: any = {}) {
    super(props);
    this._canLoadMore = props.hasOwnProperty('canLoadMore') ? props.canLoadMore : true; //(props.total && props.total > 0 && (props.items?.length || 0) < props.total) || true;
    this.archivedContactIDs = props.archivedContactIDs || Set<ID>();
    this.mergedContactsMap = props.mergedContactsMap || Map<ID, MergedContact>();
    this.sourceContactIDToMergedContactIDMap =
      props.sourceContactIDToMergedContactIDMap || Map<ID, ID>();

    if (props.items && this.archivedContactIDs.size > 0) {
      this.items = this.items.reduce((filteredItems, item) => {
        if (item.contact?.id && this.archivedContactIDs.has(item.contact.id)) {
          return filteredItems;
        }

        if (['reconnect', 'import', 'event'].includes(item.type)) {
          const filteredContacts = item.filteredContacts?.filter(
            (contact) => !this.archivedContactIDs.has(contact.id),
          );

          if (filteredContacts.length === 0) {
            return filteredItems;
          }

          return filteredItems.set(
            item.id,
            item.copyWith({ metadata: { contacts: filteredContacts } }),
          );
        }

        return filteredItems.set(item.id, item);
      }, OrderedMap<ID, ActivityItem>());
    }

    if (props.items && this.mergedContactsMap.size > 0) {
      // Birthday items aren't currently deduped in the API, they're only remapped to the resolved contact. So turning this off for now and *just* remapping.
      // // dedup birthday items -- only want one to show up per fully resolved contact
      // const birthdayItemsByContactID = this.items.reduce((birthdayItemsByContactID, item) => {
      //   if (item.type === 'birthday') {
      //     return { ...birthdayItemsByContactID, [item.contact?.id]: item }
      //   }
      //   return birthdayItemsByContactID;
      // }, {});

      // const birthdayItemsToRemove = Object.values(this.mergedContactsMap).reduce((bdayItemsPerContact, mergedContact) => {
      //   const mergedContactID = mergedContact.contact.id;
      //   let mergedContactBirthdayItems = mergedContact.sourceIDs.toArray().map((sourceID) => birthdayItemsByContactID[sourceID]).filter((item) => item);

      //   if (mergedContactBirthdayItems.length > 1) {
      //     const resolvedContactIndex = mergedContactBirthdayItems.findIndex((item) => item.metadata?.contact?.id === mergedContactID);

      //     return [...mergedContactBirthdayItems.slice(0, resolvedContactIndex), ...mergedContactBirthdayItems.slice(resolvedContactIndex + 1)].map((item) => item.id);
      //   }
      //   return bdayItemsPerContact;
      // }, []);

      // if (birthdayItemsToRemove.length > 0) {
      //   this.items = this.items.filter((item) => !birthdayItemsToRemove.includes(item.id)).toOrderedMap();
      // }

      this.items = OrderedMap(
        this.items.mapEntries(([key, item]) => {
          const metadata = item.metadata || {};
          const hasMergedContactInList = metadata.contacts?.some((contact) =>
            this.sourceContactIDToMergedContactIDMap.has(contact.id),
          );
          // People are dismissed from grouped reconnects by contact ID, so map everything but ID when correcting reconnect
          // contacts. We use linkID to override where a click on that contact will take the user. Also, we don't dedupe to
          // limit the issues of dismissing a merged contact from a grouped reconnect.
          if (item.type === 'reconnect' && hasMergedContactInList) {
            const remappedContacts = metadata.contacts.map((contact) => {
              const mergedContactID = this.sourceContactIDToMergedContactIDMap.get(contact.id);

              if (!mergedContactID) {
                return contact;
              }

              const { id, ...mergedContact } = this.mergedContactsMap.get(mergedContactID).contact;
              return { id: contact.id, linkID: id, ...mergedContact };
            });
            return [key, item.copyWith({ metadata: { ...metadata, contacts: remappedContacts } })];
          }

          // if there are contacts in the item's contacts that have been merged, map those to the merged contact
          if (hasMergedContactInList) {
            const dedupedContacts = item.metadata.contacts
              .map((contact) => {
                const mergedContactID = this.sourceContactIDToMergedContactIDMap.get(contact.id);
                return this.mergedContactsMap.get(mergedContactID)?.contact || contact;
              })
              .filter(
                (contact, index, contacts) =>
                  contacts.findIndex((c) => c.id === contact.id) === index,
              );
            return [key, item.copyWith({ metadata: { ...metadata, contacts: dedupedContacts } })];
          }

          // For individual contacts that have been merged, remap source contacts to the merged contact
          if (this.sourceContactIDToMergedContactIDMap.has(item.metadata?.contact?.id)) {
            const mergedContactID = this.sourceContactIDToMergedContactIDMap.get(
              item.metadata.contact.id,
            );
            return [
              key,
              item.copyWith({
                metadata: {
                  ...metadata,
                  contact: this.mergedContactsMap.get(mergedContactID).contact,
                },
              }),
            ];
          }

          return [key, item];
        }),
      );
    }
  }

  addArchivedContactIDs(contactIDs: ID[]): Activity {
    return this.copyWith({ archivedContactIDs: this.archivedContactIDs.union(contactIDs) });
  }

  addMergedContact(mergedContact: Contact, sourceIDs: Set<ID>) {
    return this.copyWith({
      sourceContactIDToMergedContactIDMap: this.sourceContactIDToMergedContactIDMap.merge(
        sourceIDs.reduce(
          (contactMap, sourceID) => ({ ...contactMap, [sourceID]: mergedContact.id }),
          {},
        ),
      ),
      mergedContactsMap: this.mergedContactsMap.set(mergedContact.id, {
        contact: mergedContact,
        sourceIDs,
      }),
    });
  }

  get oldestItemDate(): string {
    return String(this.items.last().date.unix);
  }

  get canLoadMore(): boolean {
    return this._canLoadMore;
  }

  get sortedItems(): ActivityItem[] {
    return this.items.sortBy((item) => -item.date.unix).toArray();
  }
}

export class FilteredActivity extends Activity {
  filter: ActivityItemType;

  constructor(props: any = {}) {
    super(props);
    this.filter = props.filter;
  }
}

export class ActivityNotifications extends Activity {
  unread_count: number;

  constructor(props: any = {}) {
    super(props);
    this.unread_count = props.unread_count;
  }

  get unreadCount(): number {
    // discount the unreads that are in the first four items
    const aboveTheFoldUnreadCount = this.items.slice(0, 4).filter((item) => !item.seen).size;
    return Math.max((this.unread_count || 0) - aboveTheFoldUnreadCount, 0);
  }
}
